import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "../../stores/store";
import useAuth from "../../hooks/useAuth";
import { observer } from "mobx-react-lite";
import useLogout from "../../hooks/useLogout";
import { axiosPrivate } from "../../api/axios";

function TopHeader({ toggleSidebar }) {
  const { mainStore } = useStore();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isHome = location.pathname === "/Home";
  const isSubcategory = location.pathname.startsWith("/Subcategory");
  const isTools = location.pathname.startsWith("/Tools");
  const isToolSelection = location.pathname.startsWith("/ToolSelection1");

  const { auth } = useAuth();
  const logout = useLogout();
  const isAdmin = auth?.role?.toLowerCase() === "admin";

  const signOut = async () => {
    await logout();
  };

  const closeMenu = () => {
    const menu = document.getElementById("topHeaderMenu");
    if (menu && menu.classList.contains("show")) {
      menu.classList.remove("show");
    }
  };

  const setLanguage = async (lang) => {
    await axiosPrivate.get(`/Home/SetLanguage/${lang}`);
    i18n.changeLanguage(lang);
    mainStore.setLang(lang);
  };

  // Style for active nav items - compact for better fit
  const getNavLinkStyle = (isActive) => ({
    color: isActive ? "#112d4e" : "#495057",
    fontWeight: isActive ? "600" : "400",
    transition: "color 0.2s ease",
    fontSize: "0.9rem",
    whiteSpace: "nowrap",
  });

  return (
    <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm py-2">
      <div className="container-fluid px-2 px-lg-3">
        {/* Sidebar Toggle - only show when not on home/browse pages */}
        {!isHome && !isSubcategory && !isTools && !isToolSelection && (
          <button
            className="navbar-toggler border-0 me-2"
            type="button"
            onClick={toggleSidebar}
          >
            <span className="navbar-toggler-icon"></span>
          </button>
        )}

        {/* Logo */}
        <Link className="navbar-brand fw-bold" to="/Home">
          <img
            src="/assets/img/OnlineCADCOM_FullLogo.png"
            alt="Online-CADCOM"
            style={{ height: "40px" }}
          />
        </Link>

        {/* Mobile Toggle */}
        <button
          className="navbar-toggler border-0"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#topHeaderMenu"
          aria-controls="topHeaderMenu"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse" id="topHeaderMenu">
          {/* Main Navigation - Left/Center */}
          {/* User order: Home, About, Tools Catalog, Tool Selection, Research, My Contributions, Help */}
          {/* Admin order: Home, About, Data Management, Tool Selection, Research, My Contributions, Help, Administration */}
          <ul className="navbar-nav me-auto mb-2 mb-lg-0">
            {/* 1. Home - for all roles */}
            <li className="nav-item">
              <Link
                to="/Home"
                className={`nav-link px-2 ${
                  mainStore.menuType === 200 ? "active" : ""
                }`}
                onClick={closeMenu}
                style={getNavLinkStyle(mainStore.menuType === 200)}
              >
                <i className="bi bi-house-door me-1"></i>
                <span className="d-inline d-lg-none d-xl-inline">{t("home")}</span>
              </Link>
            </li>

            {/* 2. About - for all roles */}
            <li className="nav-item">
              <Link
                to="/About"
                className={`nav-link px-2 ${
                  location.pathname === "/About" ? "active" : ""
                }`}
                onClick={closeMenu}
                style={getNavLinkStyle(location.pathname === "/About")}
              >
                <i className="bi bi-info-circle me-1"></i>
                <span className="d-inline d-lg-none d-xl-inline">{t("about")}</span>
              </Link>
            </li>

            {/* 3a. Tools Catalog - User role only */}
            {!isAdmin && (
              <li className="nav-item">
                <Link
                  to="/ToolsCatalog"
                  className={`nav-link px-2 ${
                    location.pathname === "/ToolsCatalog" ? "active" : ""
                  }`}
                  onClick={closeMenu}
                  style={getNavLinkStyle(location.pathname === "/ToolsCatalog")}
                >
                  <i className="bi bi-tools me-1"></i>
                  <span className="d-inline d-lg-none d-xl-inline">
                    {t("toolsCatalog")}
                  </span>
                </Link>
              </li>
            )}

            {/* 3b. Data Management - Admin role only */}
            {isAdmin && (
              <li className="nav-item">
                <Link
                  to="/TypeOfTool/List"
                  className={`nav-link px-2 ${
                    mainStore.menuType === 300 ? "active" : ""
                  }`}
                  onClick={closeMenu}
                  style={getNavLinkStyle(mainStore.menuType === 300)}
                >
                  <i className="bi bi-database me-1"></i>
                  <span className="d-inline d-lg-none d-xl-inline">
                    {t("dataManagement")}
                  </span>
                </Link>
              </li>
            )}

            {/* 4. Tool Selection - different routes for admin vs user */}
            {isAdmin ? (
              <li className="nav-item">
                <Link
                  to="/CriteriaManager"
                  className={`nav-link px-2 ${
                    mainStore.menuType === 400 ? "active" : ""
                  }`}
                  onClick={closeMenu}
                  style={getNavLinkStyle(mainStore.menuType === 400)}
                >
                  <i className="bi bi-sliders me-1"></i>
                  <span className="d-inline d-lg-none d-xl-inline">
                    {t("toolSelection")}
                  </span>
                </Link>
              </li>
            ) : (
              <li className="nav-item">
                <Link
                  to="/ToolSelection/Selection"
                  className={`nav-link px-2 ${
                    mainStore.menuType === 400 ? "active" : ""
                  }`}
                  onClick={closeMenu}
                  style={getNavLinkStyle(mainStore.menuType === 400)}
                >
                  <i className="bi bi-sliders me-1"></i>
                  <span className="d-inline d-lg-none d-xl-inline">
                    {t("toolSelection")}
                  </span>
                </Link>
              </li>
            )}

            {/* 5. Research - for all roles */}
            <li className="nav-item">
              <Link
                to="/Research/MethodComparison"
                className={`nav-link px-2 ${
                  mainStore.menuType === 500 ? "active" : ""
                }`}
                onClick={closeMenu}
                style={getNavLinkStyle(mainStore.menuType === 500)}
              >
                <i className="bi bi-graph-up me-1"></i>
                <span className="d-inline d-lg-none d-xl-inline">{t("research")}</span>
              </Link>
            </li>

            {/* 6. My Contributions - for all roles */}
            <li className="nav-item">
              <Link
                to="/MySubmissions"
                className={`nav-link px-2 ${
                  mainStore.menuType === 600 ? "active" : ""
                }`}
                onClick={closeMenu}
                style={getNavLinkStyle(mainStore.menuType === 600)}
              >
                <i className="bi bi-folder-plus me-1"></i>
                <span className="d-inline d-lg-none d-xl-inline">
                  {t("userSubmissions")}
                </span>
              </Link>
            </li>

            {/* 7. Help - for all roles */}
            <li className="nav-item">
              <Link
                to="/Help"
                className={`nav-link px-2 ${
                  location.pathname === "/Help" ? "active" : ""
                }`}
                onClick={closeMenu}
                style={getNavLinkStyle(location.pathname === "/Help")}
              >
                <i className="bi bi-question-circle me-1"></i>
                <span className="d-inline d-lg-none d-xl-inline">{t("help")}</span>
              </Link>
            </li>

            {/* 8. Administration - Admin only (last item) */}
            {isAdmin && (
              <li className="nav-item">
                <Link
                  to="/ContentApprovals/List"
                  className={`nav-link px-2 ${
                    mainStore.menuType === 100 ? "active" : ""
                  }`}
                  onClick={closeMenu}
                  style={getNavLinkStyle(mainStore.menuType === 100)}
                >
                  <i className="bi bi-gear me-1"></i>
                  <span className="d-inline d-lg-none d-xl-inline">
                    {t("administration")}
                  </span>
                </Link>
              </li>
            )}
          </ul>

          {/* Right side - Language & User Profile */}
          <ul className="navbar-nav align-items-lg-center">
            {/* Language Dropdown with Flags */}
            <li className="nav-item dropdown">
              <a
                className="nav-link dropdown-toggle px-2 d-flex align-items-center"
                href="#"
                id="dropdownLanguage"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style={{ color: "#495057", fontSize: "0.9rem" }}
              >
                {/* Current language flags */}
                <span className="d-flex align-items-center">
                  {i18n.language === "lng1" && (
                    <span className="d-flex align-items-center" style={{ gap: "2px" }}>
                      <img src="https://flagcdn.com/w20/gb.png" alt="UK" style={{ height: "14px", borderRadius: "2px" }} />
                      <img src="https://flagcdn.com/w20/us.png" alt="USA" style={{ height: "14px", borderRadius: "2px" }} />
                    </span>
                  )}
                  {i18n.language === "lng2" && (
                    <img src="https://flagcdn.com/w20/bg.png" alt="Bulgaria" style={{ height: "14px", borderRadius: "2px" }} />
                  )}
                  {i18n.language === "lng3" && (
                    <span className="d-flex align-items-center" style={{ gap: "2px" }}>
                      <img src="https://flagcdn.com/w20/al.png" alt="Albania" style={{ height: "14px", borderRadius: "2px" }} />
                      <img src="https://flagcdn.com/w20/xk.png" alt="Kosovo" style={{ height: "14px", borderRadius: "2px" }} />
                    </span>
                  )}
                </span>
                <span className="d-lg-none ms-2">{t("language")}</span>
              </a>
              <ul
                className="dropdown-menu dropdown-menu-end shadow-sm border-0"
                aria-labelledby="dropdownLanguage"
                style={{ minWidth: "180px" }}
              >
                {/* English - UK & USA flags */}
                <li>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setLanguage("lng1");
                      closeMenu();
                    }}
                    className={`dropdown-item py-2 d-flex align-items-center ${
                      i18n.language === "lng1"
                        ? "active bg-light fw-semibold"
                        : ""
                    }`}
                  >
                    <span className="d-flex align-items-center me-2" style={{ gap: "3px", minWidth: "44px" }}>
                      <img
                        src="https://flagcdn.com/w20/gb.png"
                        srcSet="https://flagcdn.com/w40/gb.png 2x"
                        alt="UK"
                        style={{ height: "16px", borderRadius: "2px", boxShadow: "0 0 1px rgba(0,0,0,0.2)" }}
                      />
                      <img
                        src="https://flagcdn.com/w20/us.png"
                        srcSet="https://flagcdn.com/w40/us.png 2x"
                        alt="USA"
                        style={{ height: "16px", borderRadius: "2px", boxShadow: "0 0 1px rgba(0,0,0,0.2)" }}
                      />
                    </span>
                    <span>{t("languageLNG1")}</span>
                    <i
                      className="bi bi-check2 ms-auto"
                      style={{
                        visibility: i18n.language === "lng1" ? "visible" : "hidden",
                        color: "#112d4e"
                      }}
                    ></i>
                  </a>
                </li>

                {/* Bulgarian - Bulgarian flag */}
                <li>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setLanguage("lng2");
                      closeMenu();
                    }}
                    className={`dropdown-item py-2 d-flex align-items-center ${
                      i18n.language === "lng2"
                        ? "active bg-light fw-semibold"
                        : ""
                    }`}
                  >
                    <span className="d-flex align-items-center me-2" style={{ minWidth: "44px" }}>
                      <img
                        src="https://flagcdn.com/w20/bg.png"
                        srcSet="https://flagcdn.com/w40/bg.png 2x"
                        alt="Bulgaria"
                        style={{ height: "16px", borderRadius: "2px", boxShadow: "0 0 1px rgba(0,0,0,0.2)" }}
                      />
                    </span>
                    <span>{t("languageLNG2")}</span>
                    <i
                      className="bi bi-check2 ms-auto"
                      style={{
                        visibility: i18n.language === "lng2" ? "visible" : "hidden",
                        color: "#112d4e"
                      }}
                    ></i>
                  </a>
                </li>

                {/* Albanian - Albania & Kosovo flags */}
                <li>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setLanguage("lng3");
                      closeMenu();
                    }}
                    className={`dropdown-item py-2 d-flex align-items-center ${
                      i18n.language === "lng3"
                        ? "active bg-light fw-semibold"
                        : ""
                    }`}
                  >
                    <span className="d-flex align-items-center me-2" style={{ gap: "3px", minWidth: "44px" }}>
                      <img
                        src="https://flagcdn.com/w20/al.png"
                        srcSet="https://flagcdn.com/w40/al.png 2x"
                        alt="Albania"
                        style={{ height: "16px", borderRadius: "2px", boxShadow: "0 0 1px rgba(0,0,0,0.2)" }}
                      />
                      <img
                        src="https://flagcdn.com/w20/xk.png"
                        srcSet="https://flagcdn.com/w40/xk.png 2x"
                        alt="Kosovo"
                        style={{ height: "16px", borderRadius: "2px", boxShadow: "0 0 1px rgba(0,0,0,0.2)" }}
                      />
                    </span>
                    <span>{t("languageLNG3")}</span>
                    <i
                      className="bi bi-check2 ms-auto"
                      style={{
                        visibility: i18n.language === "lng3" ? "visible" : "hidden",
                        color: "#112d4e"
                      }}
                    ></i>
                  </a>
                </li>
              </ul>
            </li>

            {/* Vertical Divider - Desktop only */}
            <li className="nav-item d-none d-lg-flex align-items-center mx-1">
              <span
                style={{
                  width: "1px",
                  height: "20px",
                  backgroundColor: "#dee2e6",
                }}
              ></span>
            </li>

            {/* User Profile Dropdown */}
            <li className="nav-item dropdown">
              <a
                className="nav-link dropdown-toggle px-2 d-flex align-items-center"
                href="#"
                id="dropdownProfile"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style={{ color: "#112d4e" }}
              >
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: "28px",
                    height: "28px",
                    backgroundColor: "#e8f4fd",
                    color: "#112d4e",
                    fontSize: "0.8rem",
                    fontWeight: "600",
                  }}
                >
                  {auth?.fullName?.charAt(0)?.toUpperCase() || "U"}
                </div>
                <span
                  className="fw-semibold d-inline d-lg-none d-xl-inline ms-2"
                  style={{ fontSize: "0.9rem" }}
                >
                  {auth?.fullName}
                </span>
              </a>
              <ul
                className="dropdown-menu dropdown-menu-end shadow-sm border-0"
                aria-labelledby="dropdownProfile"
                style={{ minWidth: "180px" }}
              >
                {/* User Info Header */}
                <li className="px-3 py-2 border-bottom">
                  <div className="fw-semibold" style={{ color: "#112d4e" }}>
                    {auth?.fullName}
                  </div>
                  <small className="text-muted text-capitalize">
                    {auth?.role}
                  </small>
                </li>

                {/* Logout */}
                <li className="mt-1">
                  <Link
                    to="#"
                    onClick={signOut}
                    className="dropdown-item py-2"
                    style={{ color: "#dc3545" }}
                  >
                    <i className="bi bi-box-arrow-right me-2"></i>
                    {t("profileLogout")}
                  </Link>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
}

export default observer(TopHeader);
